// Shader patch shared by every character material: one program for all rigs, whatever the team.
//
//   - A per-vertex `aPart` attribute picks the surface (cloth, glossy visor, team-coloured glow), so a
//     whole rig is one skinned mesh with one material.
//   - Rim light: a fresnel term tinted with the team colour mixed toward white, stronger on the side
//     facing away from the sun, so a player separates from bright and dark backdrops alike.
//   - Ambient floor: the unlit side of a character never falls below a fraction of its albedo.
//   - Spawn shield: while a player is spawn protected, pale bands drift up the body and the rim
//     glows (`uShield` 0..1, faded by the rig).
// No real lights are added: the rim, the floor and the shield are a few ALU ops on top of the
// standard shader.

import * as THREE from "three";
import { mix } from "./palette";

/** Values of the `aPart` vertex attribute. */
export const PART_CLOTH = 0;
export const PART_VISOR = 1;
export const PART_GLOW = 2;

export const PART_ATTRIBUTE = "aPart";

/** How far the rim colour is mixed from the team colour toward white. */
export const RIM_WHITE_MIX = 0.5;

/** Shading constants compiled into the shared program (the team colour is the only per-material input). */
export const SHADING = {
  /** Fresnel exponent: larger keeps the rim on the silhouette. */
  rimPower: 2.4,
  /** Rim strength on surfaces facing the sun. */
  rimSun: 0.2,
  /** Extra rim strength on surfaces facing directly away from the sun. */
  rimAway: 0.55,
  /** The unlit side of a character is at least this fraction of its albedo (linear). */
  ambientFloor: 0.45,
  /** Emissive strength of glow parts (a multiple of their colour). */
  glow: 1.4,
  visorRoughness: 0.12,
  visorMetalness: 0.6,
  /** Colour of the spawn-protection shimmer (linear RGB). */
  shield: [0.45, 0.8, 1.0],
  /** Bands of the shimmer per metre of body height, and how fast they climb (radians per second). */
  shieldBandsPerM: 3.2,
  shieldBandSpeed: 7.0,
} as const;

/** The rim colour of a team colour. */
export function rimTint(primary: string): string {
  return mix(primary, "#ffffff", RIM_WHITE_MIX);
}

const PROGRAM_KEY = "character-v1";

function glsl(n: number): string {
  return Number.isInteger(n) ? n.toFixed(1) : String(n);
}

/** Inserts `extra` after the first `token`; throws when three's shader no longer has it. */
export function injectAfter(
  source: string,
  token: string,
  extra: string,
): string {
  const at = source.indexOf(token);
  if (at < 0)
    throw new Error(
      `character shader: "${token}" is missing from the base shader`,
    );
  const end = at + token.length;
  return `${source.slice(0, end)}\n${extra}${source.slice(end)}`;
}

export const VERTEX_PARS = `attribute float ${PART_ATTRIBUTE};\nvarying float vPart;\nvarying float vRigY;`;
export const VERTEX_MAIN = `vPart = ${PART_ATTRIBUTE};\nvRigY = position.y;`;

export const FRAGMENT_PARS =
  "uniform vec3 uRim;\nuniform float uShield;\nuniform float uShieldPhase;\nvarying float vPart;\nvarying float vRigY;";

/** Surface selection: runs before the lighting setup reads roughness, metalness and the diffuse colour. */
export const FRAGMENT_SURFACE = `
float partVisor = step( 0.5, vPart ) - step( 1.5, vPart );
float partGlow = step( 1.5, vPart );
roughnessFactor = mix( roughnessFactor, ${glsl(SHADING.visorRoughness)}, partVisor );
metalnessFactor = mix( metalnessFactor, ${glsl(SHADING.visorMetalness)}, partVisor );
totalEmissiveRadiance += diffuseColor.rgb * ${glsl(SHADING.glow)} * partGlow;
diffuseColor.rgb *= 1.0 - 0.9 * partGlow;`;

/** Runs after all lights are accumulated. */
export const FRAGMENT_RIM = `
reflectedLight.indirectDiffuse = max( reflectedLight.indirectDiffuse, diffuseColor.rgb * ${glsl(SHADING.ambientFloor)} );
float rimFresnel = pow( 1.0 - saturate( dot( normal, geometryViewDir ) ), ${glsl(SHADING.rimPower)} );
#if NUM_DIR_LIGHTS > 0
	float rimAway = 0.5 - 0.5 * dot( normal, directionalLights[ 0 ].direction );
#else
	float rimAway = 0.5;
#endif
totalEmissiveRadiance += uRim * ( rimFresnel * ( ${glsl(SHADING.rimSun)} + ${glsl(SHADING.rimAway)} * rimAway ) * ( 1.0 - partGlow ) );
float shieldBand = 0.5 + 0.5 * sin( vRigY * ${glsl(SHADING.shieldBandsPerM * 2 * Math.PI)} - uShieldPhase );
totalEmissiveRadiance += vec3( ${SHADING.shield.map(glsl).join(", ")} ) * uShield * ( 0.55 * rimFresnel + 0.06 + 0.22 * shieldBand * shieldBand * shieldBand );`;

/** Per-material inputs of the character program. */
export interface CharacterUniforms {
  /** Spawn-protection shimmer strength, 0 (off) to 1. */
  shield: { value: number };
  /** Phase of the shimmer bands (radians). */
  shieldPhase: { value: number };
}

/** Advances the shimmer to `strength` at `timeSec` (the bands climb with time). */
export function setShield(
  u: CharacterUniforms,
  strength: number,
  timeSec: number,
): void {
  u.shield.value = strength;
  u.shieldPhase.value = (timeSec * SHADING.shieldBandSpeed) % (2 * Math.PI);
}

/**
 * Patches a MeshStandardMaterial (vertex colours on, transparent from the start: `transparent` is
 * compiled into the shader) with the character look. `primary` is the team colour; it only feeds the
 * rim uniform, so every material shares one program. Returns the material's own uniforms.
 */
export function applyCharacterShader(
  material: THREE.MeshStandardMaterial,
  primary: string,
): CharacterUniforms {
  const rim = { value: new THREE.Color(rimTint(primary)) };
  const uniforms: CharacterUniforms = {
    shield: { value: 0 },
    shieldPhase: { value: 0 },
  };
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uRim = rim;
    shader.uniforms.uShield = uniforms.shield;
    shader.uniforms.uShieldPhase = uniforms.shieldPhase;
    shader.vertexShader = injectAfter(
      injectAfter(shader.vertexShader, "#include <common>", VERTEX_PARS),
      "#include <begin_vertex>",
      VERTEX_MAIN,
    );
    shader.fragmentShader = injectAfter(
      injectAfter(
        injectAfter(shader.fragmentShader, "#include <common>", FRAGMENT_PARS),
        "#include <emissivemap_fragment>",
        FRAGMENT_SURFACE,
      ),
      "#include <lights_fragment_end>",
      FRAGMENT_RIM,
    );
  };
  material.customProgramCacheKey = () => PROGRAM_KEY;
  return uniforms;
}
